import { useCallback } from 'react';
import { Amplitude, Events } from '@iwoca/frontend-tracking-library';
import { Button } from '@iwoca/orion';
import clsx from 'clsx';
import { useApplicationTableState } from 'contexts/ApplicationTableStateContext/ApplicationTableStateContext';
import { useLocale } from 'hooks/useLocale';
import { TApplication } from 'lendingApi/applications.types';
import { TFilter } from 'types';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { useApplicationTable } from '../ApplicationTable.context';
import { FILTER_OPTIONS } from './ExternalFilters.helpers';
import styles from './ExternalFilters.module.css';

export const ExternalFilters = () => {
  const {
    state: { applications },
  } = useApplicationTable();
  const { locale } = useLocale();
  const { saveFilter, removeFilter, externalStatusFilter } =
    useApplicationTableState();
  const getStatusCount = (option: TFilter, applications?: TApplication[]) => {
    if (!applications) {
      return 0;
    }
    if (option.advancedFilter) {
      return (
        applications
          // first filter by status to reduce computation
          .filter((app) =>
            option.propertiesToFilterOn.some((status) => status === app.status),
          )
          .filter((app) => option.advancedFilter?.(app)).length || 0
      );
    }
    return (
      applications.filter((app) =>
        option.propertiesToFilterOn.some((status) => status === app.status),
      ).length || 0
    );
  };

  const renderFilter = useCallback(
    (option: TFilter) => {
      const statusCount = getStatusCount(option, applications);

      const handleClick = (option: TFilter) => {
        if (option.id === 'all') {
          removeFilter('status');
        } else {
          saveFilter('status', option.propertiesToFilterOn);
        }

        Amplitude.track(
          Events.introducerPortal.introducerPortalEventCreator({
            event: AMPLITUDE_EVENTS.quickFilterClicked,
            locale,
            payload: {
              'Filter name': option.id,
              'Application count': statusCount,
            },
          }),
        );
      };

      const isActive = option.id === externalStatusFilter;

      return (
        <li key={option.id} className={styles.filterItem}>
          <Button
            size="small"
            data-testid={isActive ? 'active-filter' : 'inactive-filter'}
            onClick={() => handleClick(option)}
            variant={isActive ? 'denim' : 'tertiary'}
          >
            {option.displayQuantity && (
              <div
                className={clsx(styles.applicationQuantity, styles[option.id])}
              >
                {statusCount}
              </div>
            )}
            {option.label}
          </Button>
        </li>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [applications, locale, externalStatusFilter],
  );

  return (
    <ul className={styles.filters}>
      {FILTER_OPTIONS().map((option) => renderFilter(option))}
    </ul>
  );
};
